import React from 'react';
import {
  FiCloud,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiClock,
  FiLink,
  FiRefreshCw,
} from 'react-icons/fi';
import { IconType } from 'react-icons';

export type SyncStatus = 'synced' | 'pending' | 'external_changes' | 'error' | 'not_linked' | 'syncing';

interface StatusConfig {
  icon: IconType;
  bg: string;
  text: string;
  border: string;
  label: string;
  tooltip: string;
}

const STATUS_CONFIG: Record<SyncStatus, StatusConfig> = {
  synced: {
    icon: FiCheckCircle,
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    label: 'Synced',
    tooltip: 'Prompt is synchronized with AWS Bedrock',
  },
  pending: {
    icon: FiClock,
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
    label: 'Pending',
    tooltip: 'Sync pending - changes will be pushed to Bedrock',
  },
  external_changes: {
    icon: FiAlertTriangle,
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    label: 'Modified',
    tooltip: 'Modified externally in AWS Bedrock',
  },
  error: {
    icon: FiXCircle,
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
    label: 'Error',
    tooltip: 'Sync error - check prompt details',
  },
  not_linked: {
    icon: FiLink,
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-500 dark:text-gray-400',
    border: 'border-gray-200 dark:border-gray-600',
    label: 'Not Linked',
    tooltip: 'Not linked to AWS Bedrock',
  },
  syncing: {
    icon: FiRefreshCw,
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    label: 'Syncing',
    tooltip: 'Synchronizing with AWS Bedrock',
  },
};

export interface BedrockMapping {
  syncing?: boolean;
  has_external_changes?: boolean;
  last_error_at?: string | null;
  consecutive_failures?: number;
  last_push_at?: string | null;
  last_pull_at?: string | null;
}

export const getSyncStatus = (mapping: BedrockMapping | null | undefined): SyncStatus => {
  if (!mapping) return 'not_linked';
  if (mapping.syncing) return 'syncing';
  if (mapping.has_external_changes) return 'external_changes';
  if (mapping.last_error_at && (mapping.consecutive_failures ?? 0) > 0) return 'error';
  if (mapping.last_push_at || mapping.last_pull_at) return 'synced';
  return 'pending';
};

type BadgeSize = 'sm' | 'md' | 'lg';

interface SizeConfig {
  container: string;
  icon: string;
  text: string;
  gap: string;
}

const SIZE_CONFIG: Record<BadgeSize, SizeConfig> = {
  sm: { container: 'px-1.5 py-0.5', icon: 'w-3 h-3', text: 'text-xs', gap: 'gap-1' },
  md: { container: 'px-2 py-1', icon: 'w-3.5 h-3.5', text: 'text-xs', gap: 'gap-1.5' },
  lg: { container: 'px-2.5 py-1.5', icon: 'w-4 h-4', text: 'text-sm', gap: 'gap-2' },
};

interface BedrockSyncBadgeProps {
  status?: SyncStatus;
  size?: BadgeSize;
  showLabel?: boolean;
  showTooltip?: boolean;
  className?: string;
}

const BedrockSyncBadge: React.FC<BedrockSyncBadgeProps> = ({
  status = 'not_linked',
  size = 'sm',
  showLabel = false,
  showTooltip = true,
  className = '',
}) => {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_linked;
  const Icon = config.icon;
  const sizes = SIZE_CONFIG[size] ?? SIZE_CONFIG.sm;

  return (
    <span
      className={`
        inline-flex items-center ${sizes.gap} ${sizes.container}
        rounded-full font-medium
        ${config.bg} ${config.text}
        ${className}
      `}
      title={showTooltip ? config.tooltip : undefined}
    >
      <Icon
        className={`${sizes.icon} ${status === 'syncing' ? 'animate-spin' : ''}`}
      />
      {showLabel && <span className={sizes.text}>{config.label}</span>}
    </span>
  );
};

interface BedrockCloudIconProps {
  linked?: boolean;
  className?: string;
}

export const BedrockCloudIcon: React.FC<BedrockCloudIconProps> = ({ linked = false, className = '' }) => {
  return (
    <FiCloud
      className={`w-4 h-4 ${
        linked
          ? 'text-blue-500 dark:text-blue-400'
          : 'text-gray-300 dark:text-gray-600'
      } ${className}`}
      title={linked ? 'Linked to AWS Bedrock' : 'Not linked to AWS Bedrock'}
    />
  );
};

interface BedrockSyncIndicatorProps {
  mapping: BedrockMapping | null | undefined;
  className?: string;
}

export const BedrockSyncIndicator: React.FC<BedrockSyncIndicatorProps> = ({ mapping, className = '' }) => {
  if (!mapping) {
    return null;
  }

  const status = getSyncStatus(mapping);
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_linked;

  const dotColors: Record<SyncStatus, string> = {
    synced: 'bg-green-500',
    pending: 'bg-amber-500',
    external_changes: 'bg-orange-500',
    error: 'bg-red-500',
    not_linked: 'bg-gray-400',
    syncing: 'bg-blue-500 animate-pulse',
  };

  return (
    <div
      className={`relative inline-flex items-center ${className}`}
      title={config.tooltip}
    >
      <FiCloud className="w-4 h-4 text-blue-500 dark:text-blue-400" />
      <span
        className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${dotColors[status]}`}
      />
    </div>
  );
};

export default BedrockSyncBadge;
